import { NavLink, useLocation } from "react-router-dom";
import { NAV_UPCOMING_VIEW_GROUP_LABEL_COPY } from "@fomofi/shared";
import { navItemFor } from "@/config/redesignNavItems";
import { cn } from "@/lib/utils";

/**
 * The pages one nav item stands for, as tabs under the page header (redesign
 * phase 1). Renders nothing on Home and on the account pages.
 *
 * Package §7:
 * - Upcoming's row is its views, Month · Pay periods · List, a `role="group"` named
 *   "Calendar view" (NavDemo). Each view is its own address, so they stay links, and the
 *   showing one says so with aria-current.
 * - Plan's row (Your plan · Debt Eliminator) is how a phone reaches Debt Eliminator: the
 *   phone keeps its four tabs (I1), and this row is its segmented control. From lg the
 *   sidebar shows the same two entries as the Plan group, so the row steps aside there
 *   while the sidebar is expanded (collapsed, the sidebar is icons only and the row
 *   stays). No badge (Debt Eliminator Spec 1.2 §4.1; Paul, 2026-10-31).
 */
export function SectionTabs() {
  const { pathname } = useLocation();
  const item = navItemFor(pathname);
  if (!item || item.sections.length < 2) return null;
  const isPlan = item.to === "/plan";
  const isUpcoming = item.to === "/calendar";
  const list = (
    // Item 11 (Paul, 2026-10-20): the tabs wrap onto a second row instead of scrolling
    // the strip sideways. At 390 the Plan row already hid "Debt Eliminator" off the edge,
    // and at 200% text four of its five tabs, so a section was one swipe away and unseen.
    <ul
      role="list"
      data-tab-row
      className="inline-flex max-w-full flex-wrap gap-1 rounded-xl bg-muted p-1"
    >
      {item.sections.map((section) => {
        return (
          <li key={section.to} role="listitem">
            <NavLink
              to={section.to}
              end
              className={({ isActive }) =>
                cn(
                  // 44px tall (package §7: the views are min-height 44; the native-feel
                  // rule's target size on a phone). Not `tap-target`: its 24px min-height
                  // comes later in the utilities layer and won (flow pass: 32px at 390).
                  "pkg-focus flex min-h-11 items-center gap-2 whitespace-nowrap rounded-lg px-4 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-card font-semibold text-foreground shadow-sm"
                    : "font-medium text-muted-foreground hover:text-foreground",
                )
              }
            >
              {section.label}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
  if (isUpcoming) {
    return (
      <div role="group" aria-label={NAV_UPCOMING_VIEW_GROUP_LABEL_COPY} className="mb-4">
        {list}
      </div>
    );
  }
  return (
    <nav
      aria-label={`${item.label} pages`}
      className="mb-4"
      // index.css: hidden from lg while the sidebar is expanded (it shows the Plan group);
      // a collapsed sidebar is icons only, so the row stays there.
      data-plan-section-tabs={isPlan ? "" : undefined}
    >
      {list}
    </nav>
  );
}
